/**
 * Cart Progress Bar & Free Gift Manager
 * Handles automatic free gift addition/removal based on cart threshold
 * Also handles certificate removal when parent products are removed
 */

// Helper function for logging errors
const logError = (message, error, context) => {
  if (typeof logger !== 'undefined') {
    logger.error(message, error, context || 'CartProgressBar');
  } else {
    console.error(message, error);
  }
};

// Certificate variant ID - defined in constants.js (loaded before this script)
// Fallback value for safety (should not be needed)
if (typeof CERTIFICATE_VARIANT_ID === 'undefined') {
  window.CERTIFICATE_VARIANT_ID = 45420259705055;
}

// Country codes for free shipping and free gift eligibility (can be different)
// Leave blank ('') to enable globally for all markets
const FREE_SHIPPING_COUNTRY_CODE = 'IN';
const FREE_GIFT_COUNTRY_CODE = 'IN';

/**
 * CartProgressBar class manages cart progress display, free gift addition/removal,
 * and certificate cleanup based on cart contents and thresholds
 */
class CartProgressBar {
  /**
   * Initialize CartProgressBar instance
   * @constructor
   */
  constructor() {
    this.wrapper = document.querySelector('.cart-progress-bar-wrapper');
    if (!this.wrapper) return;

    this.config = {
      freeShippingEnabled: this.wrapper.dataset.freeShippingEnabled === 'true',
      freeGiftEnabled: this.wrapper.dataset.freeGiftEnabled === 'true',
      freeShippingThreshold: parseInt(this.wrapper.dataset.freeShippingThreshold) || 0,
      freeGiftThreshold: parseInt(this.wrapper.dataset.freeGiftThreshold) || 0,
      freeGiftProductId: this.wrapper.dataset.freeGiftProductId,
      freeGiftInCart: this.wrapper.dataset.freeGiftInCart === 'true',
      currencySymbol: this.wrapper.dataset.currencySymbol || '$',
      countryCode: this.wrapper.dataset.countryCode || ''
    };

    this.isProcessing = false;
    this.init();
  }

  init() {
    // Subscribe to cart updates
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.handleCartUpdate(event);
      });
    }

    // Initial check for free gift
    this.checkFreeGiftStatus();
  }

  async handleCartUpdate(event) {
    if (this.isProcessing) {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('CartProgressBar: Skipping cart update - already processing');
      }
      return;
    }
    
    // Ignore our own events to prevent loops
    if (event.source === 'cart-progress-bar') return;
    
    // Don't process if checkout is in progress (prevent interference with checkout)
    if (window.checkoutInProgress) {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('CartProgressBar: Skipping cart update - checkout in progress');
      }
      return;
    }

    const cartData = event.cartData;
    if (!cartData) {
      // If no cart data in event, fetch it directly
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const freshCartData = await response.json();
        await this.processCartUpdate(freshCartData.items);
      } catch (error) {
        logError('Error fetching cart data', error, 'handleCartUpdate');
      }
      return;
    }

    await this.processCartUpdate(cartData.items);
  }

  /**
   * Process cart items and manage free gifts/certificates
   * @param {Array} items - Array of cart item objects
   */
  async processCartUpdate(items) {
    // Analyze cart contents
    const cartAnalysis = this.analyzeCart(items);

    this.config.freeGiftInCart = cartAnalysis.freeGiftInCart;

    // Debug logging for cart analysis
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('CartProgressBar: Processing cart update', {
        cartTotal: cartAnalysis.cartTotal,
        regularItemCount: cartAnalysis.regularItemCount,
        freeGiftInCart: cartAnalysis.freeGiftInCart,
        freeGiftLineKey: cartAnalysis.freeGiftLineKey,
        orphanedCertificates: cartAnalysis.orphanedCertificateKeys.length
      });
    }

    // If no regular products, remove all special items (certificates and free gifts)
    if (cartAnalysis.regularItemCount === 0) {
      if (cartAnalysis.freeGiftInCart || cartAnalysis.certificateLineKeys.length > 0) {
        await this.removeAllSpecialItems(cartAnalysis);
      }
      return;
    }

    // Remove orphaned certificates (certificates whose parent products were removed)
    if (cartAnalysis.orphanedCertificateKeys.length > 0) {
      await this.removeOrphanedCertificates(cartAnalysis.orphanedCertificateKeys);
      return; // Will refresh and re-check after removal
    }

    // Check if we need to add or remove free gift
    // Only allow free gift for eligible country (or all countries if blank)
    const isFreeGiftCountry = FREE_GIFT_COUNTRY_CODE === '' || this.config.countryCode === FREE_GIFT_COUNTRY_CODE;
    
    if (this.config.freeGiftEnabled && this.config.freeGiftProductId && isFreeGiftCountry) {
      // Only have free gift if cart total meets threshold AND there are regular items
      const shouldHaveFreeGift = cartAnalysis.cartTotal >= this.config.freeGiftThreshold && cartAnalysis.regularItemCount > 0;

      if (shouldHaveFreeGift && !cartAnalysis.freeGiftInCart) {
        // Debug logging
        console.log('CartProgressBar: Conditions met - attempting to add free gift', {
          cartTotal: cartAnalysis.cartTotal,
          threshold: this.config.freeGiftThreshold,
          regularItemCount: cartAnalysis.regularItemCount,
          freeGiftInCart: cartAnalysis.freeGiftInCart,
          variantId: this.config.freeGiftProductId,
          freeGiftEnabled: this.config.freeGiftEnabled,
          isFreeGiftCountry: isFreeGiftCountry
        });
        await this.addFreeGift();
      } else if (!shouldHaveFreeGift && cartAnalysis.freeGiftInCart && cartAnalysis.freeGiftLineKey) {
        console.log('CartProgressBar: Conditions not met - removing free gift', {
          cartTotal: cartAnalysis.cartTotal,
          threshold: this.config.freeGiftThreshold,
          shouldHaveFreeGift: shouldHaveFreeGift,
          freeGiftInCart: cartAnalysis.freeGiftInCart
        });
        await this.removeFreeGift(cartAnalysis.freeGiftLineKey);
      } else {
        // Debug logging when conditions don't match
        if (typeof console !== 'undefined' && console.debug) {
          console.debug('CartProgressBar: Free gift status unchanged', {
            shouldHaveFreeGift: shouldHaveFreeGift,
            freeGiftInCart: cartAnalysis.freeGiftInCart,
            cartTotal: cartAnalysis.cartTotal,
            threshold: this.config.freeGiftThreshold
          });
        }
      }
    } else {
      // Debug logging for why free gift isn't being processed
      if (typeof console !== 'undefined' && console.log) {
        console.log('CartProgressBar: Free gift conditions not met', {
          freeGiftEnabled: this.config.freeGiftEnabled,
          freeGiftProductId: this.config.freeGiftProductId,
          isFreeGiftCountry: isFreeGiftCountry,
          countryCode: this.config.countryCode,
          requiredCountry: FREE_GIFT_COUNTRY_CODE
        });
      }
      
      if (!isFreeGiftCountry && cartAnalysis.freeGiftInCart && cartAnalysis.freeGiftLineKey) {
        // Remove free gift if country is not eligible
        await this.removeFreeGift(cartAnalysis.freeGiftLineKey);
      }
    }
  }

  /**
   * Remove orphaned certificates whose parent products are no longer in cart
   * @param {Array<string>} orphanedKeys - Array of line item keys for orphaned certificates
   */
  async removeOrphanedCertificates(orphanedKeys) {
    if (this.isProcessing || orphanedKeys.length === 0) return;
    this.isProcessing = true;

    try {
      const updates = {};
      for (const key of orphanedKeys) {
        updates[key] = 0;
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        this.refreshCart();
      }
    } catch (error) {
      logError('Error removing orphaned certificates', error, 'removeOrphanedCertificates');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Analyze cart items and categorize them into regular items, free gifts, and certificates
   * @param {Array} items - Array of cart item objects
   * @returns {Object} Analysis object containing:
   *   - cartTotal: {number} Total price of regular items
   *   - freeGiftInCart: {boolean} Whether free gift is present
   *   - freeGiftLineKey: {string|null} Key of free gift line item
   *   - regularItemCount: {number} Count of regular (non-gift) items
   *   - certificateLineKeys: {Array<string>} Keys of certificate line items
   *   - orphanedCertificateKeys: {Array<string>} Keys of orphaned certificates
   */
  analyzeCart(items) {
    if (!items || !Array.isArray(items)) {
      return {
        cartTotal: 0,
        freeGiftInCart: false,
        freeGiftLineKey: null,
        regularItemCount: 0,
        certificateLineKeys: [],
        orphanedCertificateKeys: []
      };
    }
    let cartTotal = 0;
    let freeGiftInCart = false;
    let freeGiftLineKey = null;
    let regularItemCount = 0;
    let certificateLineKeys = [];
    let certificates = [];
    let regularProductTitles = [];

    for (const item of items) {
      // Check if item is a free gift
      // Handle both object and array property formats
      let isFreeGift = false;
      if (item.properties) {
        if (Array.isArray(item.properties)) {
          // Properties as array (Liquid format)
          isFreeGift = item.properties.some(prop => 
            (prop.first === '_is_free_gift' && prop.last === 'true') ||
            (prop.key === '_is_free_gift' && prop.value === 'true')
          );
        } else {
          // Properties as object (JSON API format)
          isFreeGift = item.properties._is_free_gift === 'true';
        }
      }
      
      // Check if item is a certificate (by variant ID or by having 'Purchased For' property)
      let hasPurchasedFor = false;
      if (item.properties) {
        if (Array.isArray(item.properties)) {
          hasPurchasedFor = item.properties.some(prop => 
            prop.first === 'Purchased For' || prop.key === 'Purchased For'
          );
        } else {
          hasPurchasedFor = !!item.properties['Purchased For'];
        }
      }
      const isCertificate = item.variant_id === CERTIFICATE_VARIANT_ID || hasPurchasedFor;
      
      if (isFreeGift) {
        freeGiftInCart = true;
        freeGiftLineKey = item.key;
      } else if (isCertificate) {
        certificateLineKeys.push(item.key);
        // Extract 'Purchased For' value
        let purchasedFor = null;
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            const purchasedForProp = item.properties.find(prop => 
              prop.first === 'Purchased For' || prop.key === 'Purchased For'
            );
            purchasedFor = purchasedForProp ? (purchasedForProp.last || purchasedForProp.value) : null;
          } else {
            purchasedFor = item.properties['Purchased For'] || null;
          }
        }
        certificates.push({
          key: item.key,
          purchasedFor: purchasedFor
        });
      } else {
        cartTotal += item.final_line_price;
        regularItemCount += item.quantity;
        // Store product title for matching with certificates
        regularProductTitles.push(item.product_title);
      }
    }

    // Find orphaned certificates (certificates whose parent products are no longer in cart)
    const orphanedCertificateKeys = certificates
      .filter(cert => {
        if (!cert.purchasedFor) return false;
        // Check if any regular product title matches the certificate's 'Purchased For' value
        // The 'Purchased For' value is the page title which includes the product title
        return !regularProductTitles.some(title => cert.purchasedFor.includes(title));
      })
      .map(cert => cert.key);

    return {
      cartTotal,
      freeGiftInCart,
      freeGiftLineKey,
      regularItemCount,
      certificateLineKeys,
      orphanedCertificateKeys
    };
  }

  /**
   * Remove all special items (free gifts and certificates) when no regular products exist
   * @param {Object} cartAnalysis - Analysis object from analyzeCart()
   */
  async removeAllSpecialItems(cartAnalysis) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const itemsToRemove = [];
    
    if (cartAnalysis.freeGiftLineKey) {
      itemsToRemove.push({ id: cartAnalysis.freeGiftLineKey, quantity: 0 });
    }
    
    for (const certKey of cartAnalysis.certificateLineKeys) {
      itemsToRemove.push({ id: certKey, quantity: 0 });
    }

    if (itemsToRemove.length === 0) {
      this.isProcessing = false;
      return;
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates: Object.fromEntries(itemsToRemove.map(item => [item.id, 0])) })
      });

      if (response.ok) {
        this.refreshCart();
      }
    } catch (error) {
      logError('Error removing special items', error, 'removeAllSpecialItems');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Check current free gift status and add/remove as needed
   */
  async checkFreeGiftStatus() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();

      // Analyze cart contents
      const cartAnalysis = this.analyzeCart(cart.items);

      // If no regular products, remove all special items (certificates and free gifts)
      if (cartAnalysis.regularItemCount === 0 && 
          (cartAnalysis.freeGiftInCart || cartAnalysis.certificateLineKeys.length > 0)) {
        await this.removeAllSpecialItems(cartAnalysis);
        return;
      }

      // Remove orphaned certificates (certificates whose parent products were removed)
      if (cartAnalysis.orphanedCertificateKeys.length > 0) {
        await this.removeOrphanedCertificates(cartAnalysis.orphanedCertificateKeys);
        return; // Will refresh and re-check after removal
      }

      // Only proceed with free gift logic if enabled
      if (!this.config.freeGiftEnabled || !this.config.freeGiftProductId) return;

      // Only allow free gift for eligible country (or all countries if blank)
      const isFreeGiftCountry = FREE_GIFT_COUNTRY_CODE === '' || this.config.countryCode === FREE_GIFT_COUNTRY_CODE;
      
      if (!isFreeGiftCountry) {
        // Remove free gift if country is not eligible
        if (cartAnalysis.freeGiftInCart && cartAnalysis.freeGiftLineKey) {
          await this.removeFreeGift(cartAnalysis.freeGiftLineKey);
        }
        return;
      }

      // Only have free gift if cart total meets threshold AND there are regular items
      const shouldHaveFreeGift = cartAnalysis.cartTotal >= this.config.freeGiftThreshold && cartAnalysis.regularItemCount > 0;

      if (shouldHaveFreeGift && !cartAnalysis.freeGiftInCart) {
        await this.addFreeGift();
      } else if (!shouldHaveFreeGift && cartAnalysis.freeGiftInCart && cartAnalysis.freeGiftLineKey) {
        await this.removeFreeGift(cartAnalysis.freeGiftLineKey);
      }
    } catch (error) {
      logError('Error checking free gift status', error, 'checkFreeGiftStatus');
    }
  }

  /**
   * Add free gift to cart if threshold is met and country is eligible
   */
  async addFreeGift() {
    // Prevent adding free gift if country is not eligible (unless global)
    const isFreeGiftCountry = FREE_GIFT_COUNTRY_CODE === '' || this.config.countryCode === FREE_GIFT_COUNTRY_CODE;
    
    // Validate inputs before proceeding
    if (this.isProcessing) {
      logError('Already processing free gift addition', null, 'addFreeGift');
      return;
    }
    
    if (!this.config.freeGiftProductId) {
      logError('Free gift product ID is missing', { config: this.config }, 'addFreeGift');
      return;
    }
    
    const variantId = parseInt(this.config.freeGiftProductId);
    if (isNaN(variantId) || variantId <= 0) {
      logError('Invalid free gift product ID', { productId: this.config.freeGiftProductId, parsed: variantId }, 'addFreeGift');
      return;
    }
    
    if (!isFreeGiftCountry) {
      logError('Country not eligible for free gift', { countryCode: this.config.countryCode, required: FREE_GIFT_COUNTRY_CODE }, 'addFreeGift');
      return;
    }
    
    this.isProcessing = true;

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            '_is_free_gift': 'true'
          }
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const responseData = await response.json();

      // Check for errors in response (Shopify can return 200 with errors in body)
      if (!response.ok || responseData.status || responseData.errors) {
        const errorMessage = responseData.description || responseData.message || 'Unknown error';
        console.error('CartProgressBar: Failed to add free gift to cart', {
          status: response.status,
          response: responseData,
          variantId: variantId,
          errorMessage: errorMessage
        });
        logError('Failed to add free gift to cart', {
          status: response.status,
          response: responseData,
          variantId: variantId
        }, 'addFreeGift');
        await this.refreshCart();
        return;
      }

      // Success - log for debugging
      console.log('CartProgressBar: Successfully added free gift to cart', {
        variantId: variantId,
        response: responseData
      });

      // Small delay to ensure cart is updated on server
      const delay = typeof TIMING !== 'undefined' ? TIMING.FREE_GIFT_ADD_DELAY : 300;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Refresh cart sections - this will create a new CartProgressBar instance
      await this.refreshCart();
    } catch (error) {
      logError('Error adding free gift', error, 'addFreeGift');
      // Still refresh to show current state
      await this.refreshCart();
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove free gift from cart
   * @param {string} lineKey - Line item key of the free gift to remove
   */
  async removeFreeGift(lineKey) {
    if (this.isProcessing || !lineKey) return;
    this.isProcessing = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (response.ok) {
        // Refresh cart sections
        this.refreshCart();
      }
    } catch (error) {
      logError('Error removing free gift', error, 'removeFreeGift');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Refresh cart drawer and icon bubble, reinitialize progress bar
   */
  async refreshCart() {
    // Fetch fresh cart data first
    let cartData = null;
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error(`HTTP error! status: ${cartResponse.status}`);
      }
      cartData = await cartResponse.json();
    } catch (error) {
      logError('Error fetching cart for refresh', error, 'refreshCart');
    }

    // Reload cart drawer or page content
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      try {
        // Use the cart drawer's getSectionsToRender if available
        const sectionsToRender = cartDrawer.getSectionsToRender ? 
          cartDrawer.getSectionsToRender().map(s => s.id) : 
          ['cart-drawer'];
        
        const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        
        const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart-progress-bar-wrapper'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        // Reinitialize progress bar with updated data
        new CartProgressBar();
        
        // Trigger cart:refresh event for other components
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (e) {
        logError('Error refreshing cart drawer', e, 'refreshCart');
      }
    }

    // Update cart icon bubble
    const cartIconBubble = document.querySelector('cart-icon-bubble');
    if (cartIconBubble) {
      try {
        const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const sourceElement = html.querySelector('.shopify-section');
        if (sourceElement) {
          cartIconBubble.innerHTML = sourceElement.innerHTML;
        }
      } catch (e) {
        logError('Error refreshing cart icon', e, 'refreshCart');
      }
    }

    // Trigger cart update event after refresh with fresh cart data
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && cartData) {
      publish(PUB_SUB_EVENTS.cartUpdate, { 
        source: 'cart-progress-bar', 
        cartData: cartData 
      });
    }
  }
}

/**
 * Cart Cleanup Manager - handles cleanup even when progress bar is disabled
 * Removes orphaned certificates and free gifts when no regular products exist
 */
class CartCleanupManager {
  constructor() {
    this.isProcessing = false;
    this.init();
  }

  init() {
    // Subscribe to cart updates
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-cleanup-manager') return;
        this.handleCartUpdate(event);
      });
    }

    // Initial check
    this.checkAndCleanup();
  }

  async handleCartUpdate(event) {
    if (this.isProcessing) return;
    const cartData = event.cartData;
    if (!cartData) return;
    await this.cleanupCart(cartData.items);
  }

  async checkAndCleanup() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      await this.cleanupCart(cart.items);
    } catch (error) {
      logError('Error checking cart for cleanup', error, 'checkAndCleanup');
    }
  }

  async cleanupCart(items) {
    if (this.isProcessing) return;
    if (!items || !Array.isArray(items)) return;

    let regularItemCount = 0;
    let regularProductTitles = [];
    let freeGiftLineKey = null;
    let certificateLineKeys = [];
    let certificates = [];

    for (const item of items) {
      // Check if item is a free gift - handle both object and array property formats
      let isFreeGift = false;
      if (item.properties) {
        if (Array.isArray(item.properties)) {
          isFreeGift = item.properties.some(prop => 
            (prop.first === '_is_free_gift' && prop.last === 'true') ||
            (prop.key === '_is_free_gift' && prop.value === 'true')
          );
        } else {
          isFreeGift = item.properties._is_free_gift === 'true';
        }
      }
      
      // Check if item is a certificate
      let hasPurchasedFor = false;
      if (item.properties) {
        if (Array.isArray(item.properties)) {
          hasPurchasedFor = item.properties.some(prop => 
            prop.first === 'Purchased For' || prop.key === 'Purchased For'
          );
        } else {
          hasPurchasedFor = !!item.properties['Purchased For'];
        }
      }
      const isCertificate = item.variant_id === CERTIFICATE_VARIANT_ID || hasPurchasedFor;

      if (isFreeGift) {
        freeGiftLineKey = item.key;
      } else if (isCertificate) {
        certificateLineKeys.push(item.key);
        // Extract 'Purchased For' value
        let purchasedFor = null;
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            const purchasedForProp = item.properties.find(prop => 
              prop.first === 'Purchased For' || prop.key === 'Purchased For'
            );
            purchasedFor = purchasedForProp ? (purchasedForProp.last || purchasedForProp.value) : null;
          } else {
            purchasedFor = item.properties['Purchased For'] || null;
          }
        }
        certificates.push({
          key: item.key,
          purchasedFor: purchasedFor
        });
      } else {
        regularItemCount += item.quantity;
        regularProductTitles.push(item.product_title);
      }
    }

    const itemsToRemove = [];

    // If no regular products, remove all special items
    if (regularItemCount === 0) {
      if (freeGiftLineKey) itemsToRemove.push(freeGiftLineKey);
      itemsToRemove.push(...certificateLineKeys);
    } else {
      // Find orphaned certificates
      for (const cert of certificates) {
        if (cert.purchasedFor && !regularProductTitles.some(title => cert.purchasedFor.includes(title))) {
          itemsToRemove.push(cert.key);
        }
      }
    }

    if (itemsToRemove.length === 0) return;

    this.isProcessing = true;
    try {
      const updates = {};
      for (const key of itemsToRemove) {
        updates[key] = 0;
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        // Trigger refresh
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const cartResponse = await fetch('/cart.js');
          if (!cartResponse.ok) {
            throw new Error(`HTTP error! status: ${cartResponse.status}`);
          }
          const cartData = await cartResponse.json();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cleanup-manager', cartData });
        }
      }
    } catch (error) {
      logError('Error cleaning up cart', error, 'cleanupCart');
    } finally {
      this.isProcessing = false;
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Always initialize cleanup manager
  new CartCleanupManager();
  // Initialize progress bar if wrapper exists
  new CartProgressBar();
});

// Re-initialize after cart drawer opens
document.addEventListener('cart-drawer-opened', () => {
  new CartProgressBar();
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CartProgressBar, CartCleanupManager };
}

